import type { ColumnDef } from '@tanstack/react-table'
import { ProjectBadge } from '@/features/projects/components/project-badge'
import { DataTableColumnHeader } from '@/shared/components/data-table'
import { cn } from '@/shared/lib/utils'
import { formatHumanDate } from '@/shared/utils/format-date'
import type { TypePropertyAdminRow } from '../models/type-property-admin'

export const TYPE_PROPERTIES_ADMIN_COLUMN_NAMES: Record<string, string> = {
  code: 'Código',
  name: 'Nombre',
  projectName: 'Proyecto',
  active: 'Estado',
  updatedAt: 'Actualizado'
}

/**
 * Badge sobrio estilo Shopify: píldora neutra (gris) con un punto de color
 * discreto — el color vive en el punto, no grita en el fondo.
 */
export function TypePropertyActiveBadge({ active }: { active: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
      <span
        aria-hidden="true"
        className={cn(
          'h-1.5 w-1.5 shrink-0 rounded-full',
          active ? 'bg-emerald-500' : 'bg-neutral-400'
        )}
      />
      {active ? 'Activo' : 'Inactivo'}
    </span>
  )
}

export const typePropertiesAdminColumns: ColumnDef<TypePropertyAdminRow>[] = [
  {
    accessorKey: 'code',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[120px]" column={column} name="Código" />
    ),
    cell: ({ row }) => <span className="whitespace-nowrap font-medium">{row.original.code}</span>
  },
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[200px]" column={column} name="Nombre" />
    ),
    cell: ({ row }) =>
      row.original.name ? (
        <span className="truncate" title={row.original.name}>
          {row.original.name}
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      )
  },
  {
    accessorKey: 'projectName',
    header: ({ column }) => <DataTableColumnHeader column={column} name="Proyecto" />,
    cell: ({ row }) => (
      <ProjectBadge name={row.original.projectName} code={row.original.projectCode} />
    )
  },
  {
    accessorKey: 'active',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Estado" sortable={false} />
    ),
    cell: ({ row }) => <TypePropertyActiveBadge active={row.original.active} />
  },
  {
    accessorKey: 'updatedAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Actualizado" sortable={false} />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatHumanDate(row.original.updatedAt)}
      </span>
    )
  }
]
